import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { SUBJECTS, Subject } from '../../../data/academicTaxonomy';
import { VELLUM as T, SANS, MONO, mono } from '../folio/kit';

/* Adding subjects, in the folio vocabulary.

   The chosen subjects are tokens struck into the field rather than pills:
   square, ink-filled, mono - the same mark the sheet uses everywhere else
   for a fixed value. The schedule below is ruled and alphabetised, and each
   row carries the folio checkbox rather than a rounded blue tick.

   `bare` drops the control's own box for callers that already draw a cell
   around it. See JobTitleCombobox, which takes the same prop for the same
   reason. */

const SP = 'sms';

interface SubjectMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options?: ReadonlyArray<Subject>;
  placeholder?: string;
  disabled?: boolean;
  /* Drop the control's own border and ground; the caller draws the cell. */
  bare?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

interface DisplayRow {
  type: 'divider' | 'item';
  letter?: string;
  subject?: Subject;
}

const SubjectMultiSelect: React.FC<SubjectMultiSelectProps> = ({
  value,
  onChange,
  options = SUBJECTS,
  placeholder = 'Search subjects…',
  disabled = false,
  bare = false,
  id,
  name,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const rows = useMemo<DisplayRow[]>(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? [...options]
          .filter((s) => s.name.toLowerCase().includes(q))
          .sort((a, b) => {
            const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
            const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return a.name.localeCompare(b.name);
          })
      : [...options].sort((a, b) => a.name.localeCompare(b.name));

    const result: DisplayRow[] = [];
    let lastLetter = '';
    for (const s of filtered) {
      const letter = s.name.charAt(0).toUpperCase();
      if (letter !== lastLetter) {
        result.push({ type: 'divider', letter });
        lastLetter = letter;
      }
      result.push({ type: 'item', subject: s });
    }
    return result;
  }, [options, query]);

  const itemIndices = useMemo(
    () => rows.map((r, i) => (r.type === 'item' ? i : -1)).filter((i) => i >= 0),
    [rows]
  );

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const toggleSubject = (subject: Subject) => {
    if (value.includes(subject.name)) {
      onChange(value.filter((v) => v !== subject.name));
    } else {
      onChange([...value, subject.name]);
    }
  };

  const removeSubject = (name: string) => {
    onChange(value.filter((v) => v !== name));
  };

  const moveActive = (direction: 1 | -1) => {
    if (itemIndices.length === 0) return;
    const currentPos = itemIndices.indexOf(activeIndex);
    let nextPos = currentPos + direction;
    if (nextPos < 0) nextPos = itemIndices.length - 1;
    if (nextPos >= itemIndices.length) nextPos = 0;
    setActiveIndex(itemIndices[nextPos]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0) {
        e.preventDefault();
        const row = rows[activeIndex];
        if (row?.subject) toggleSubject(row.subject);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ position: 'relative' }}>
      <div
        className={`${SP}-box${bare ? '' : ` ${SP}-boxed`}`}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          border: bare ? 'none' : `1.5px solid ${T.ink}`,
          background: bare ? 'transparent' : T.ground,
          padding: bare ? '4px 0' : '6px 12px',
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="11" cy="11" r="6.5" stroke={T.mute} strokeWidth="1.6" />
          <path d="m20 20-4-4" stroke={T.mute} strokeWidth="1.6" strokeLinecap="round" />
        </svg>

        {value.map((subjectName) => (
          <span
            key={subjectName}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: T.ink,
              color: T.ground,
              padding: '4px 4px 4px 8px',
              fontFamily: MONO,
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              maxWidth: 220,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subjectName}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSubject(subjectName);
                }}
                aria-label={`Remove ${subjectName}`}
                className={`${SP}-x`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 15,
                  height: 15,
                  padding: 0,
                  border: 'none',
                  borderRadius: 0,
                  background: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <svg aria-hidden width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path d="M1 1l6 6M7 1 1 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
                </svg>
              </button>
            )}
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id || 'subjects'}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={query}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={handleKeyDown}
          className={`${SP}-input`}
          style={{
            flex: 1,
            minWidth: 120,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '7px 0',
            fontFamily: SANS,
            fontSize: 15.5,
            color: T.ink,
          }}
        />

        <svg
          aria-hidden
          width="9"
          height="6"
          viewBox="0 0 9 6"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
        >
          <path d="M1 1L4.5 4.5L8 1" stroke={T.mute} strokeWidth="1.3" strokeLinecap="square" />
        </svg>
      </div>

      {open && (
        <ul
          ref={listRef}
          id={`${id || 'subjects'}-listbox`}
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute',
            zIndex: 30,
            top: '100%',
            left: 0,
            marginTop: 4,
            width: '100%',
            maxHeight: 288,
            overflowY: 'auto',
            listStyle: 'none',
            padding: 0,
            border: `1.5px solid ${T.ink}`,
            background: T.ground,
          }}
        >
          {rows.length === 0 ? (
            <li style={{ ...mono(T), padding: '16px 13px', textAlign: 'center', letterSpacing: '0.1em' }}>
              No subject found
            </li>
          ) : (
            rows.map((row, index) => {
              if (row.type === 'divider') {
                return (
                  <li
                    key={`div-${row.letter}`}
                    data-row-index={index}
                    aria-hidden="true"
                    style={{
                      ...mono(T, T.accent),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '9px 13px 5px',
                      userSelect: 'none',
                    }}
                  >
                    <span>{row.letter}</span>
                    <span style={{ flex: 1, height: 1, background: T.rule }} />
                  </li>
                );
              }
              const isActive = index === activeIndex;
              const isSelected = value.includes(row.subject!.name);
              return (
                <li
                  key={row.subject!.name}
                  role="option"
                  aria-selected={isSelected}
                  data-row-index={index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleSubject(row.subject!);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    cursor: 'pointer',
                    padding: '8px 13px',
                    fontFamily: SANS,
                    fontSize: 14.5,
                    color: T.ink,
                    fontWeight: isSelected ? 600 : 400,
                    background: isActive ? 'rgba(10,110,126,0.08)' : 'transparent',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {/* The folio checkbox: a square that fills with ink. */}
                    <span
                      aria-hidden="true"
                      style={{
                        width: 15,
                        height: 15,
                        flexShrink: 0,
                        boxSizing: 'border-box',
                        border: `1.5px solid ${isSelected ? T.ink : T.rule}`,
                        background: isSelected ? T.ink : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isSelected && (
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                          <path
                            d="M1 5l3 3 5-6"
                            stroke={T.ground}
                            strokeWidth="1.8"
                            strokeLinecap="square"
                          />
                        </svg>
                      )}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.subject!.name}
                    </span>
                  </span>
                  <span
                    title={row.subject!.category}
                    style={{
                      ...mono(T),
                      fontSize: 9.5,
                      flexShrink: 0,
                      maxWidth: '40%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.subject!.category}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}

      <style>{`
        .${SP}-input::placeholder { color: ${T.mute}; opacity: 1; }
        /* Only the control that draws its own box takes the focus ring; see
           JobTitleCombobox for the same note. */
        .${SP}-boxed:focus-within { box-shadow: inset 0 0 0 1.5px ${T.accent}; }
        .${SP}-x:hover { background: ${T.accent}; }
        .${SP}-x:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 1px; }
      `}</style>
    </div>
  );
};

export default SubjectMultiSelect;
